import {Dropdown, Form} from './';
import React, {useState} from 'react';
import {FormikValues} from "formik/dist/types";
import { useIntl } from '../_library/intl';


type Props = {
    disabled?: boolean,
    btnLabel: string,
    onFilterUpdate?: (values: FormikValues) => void,
    filters: {
        type: string, name?: string, label?: string, options?: {label: string, value: string}[], 
        fields?: {type: string, name: string, label: string}[],
        inputPlaceholder?: string, inputLabel?: string, inputOptions?: {label?: string, display: string, value: string}[],
        inputNotLabels?: boolean}[],
    search?: string,
    resetFilter?: () => void,
    fullHeight?: boolean,
    panelPosition?: 'left' | 'right',
    panelWidthClassName?: string,
};

export const Filter: (props: Props) => JSX.Element =
    ({
         disabled,
         btnLabel,
         onFilterUpdate,
         filters,
         search: searchProvided,
         resetFilter,
         fullHeight,
         panelPosition,
         panelWidthClassName
    }) => {

    const [activeFilters, setActiveFilters] = useState(0);
    const [submittedValues, setSubmittedValues] = useState<FormikValues>({});
    const intl = useIntl();

    const handleSubmit = (values: FormikValues, actions: any, close?: () => void) => {
        actions.setSubmitting(true);
        setSubmittedValues(values);
        if (onFilterUpdate) {
            onFilterUpdate(values);
        }

        let selectedFilters = 0;
        Object.keys(values).forEach(key => {
            const val = values[key];
            if (val && val !== '' && !(Array.isArray(val) && val.length === 0)) {
                selectedFilters += 1;
            }
        });
        setActiveFilters(selectedFilters);
        actions.setSubmitting(false);
        close?.();
    };

    return (
        <>
            <div className="flex flex-wrap items-center mb-2 gap-2">
                {Object.entries(submittedValues).filter(([_, value]) => value && value !== '' && !(Array.isArray(value) && value.length === 0)).map(([key, value]) => {
                    const filter = filters.find(f => f.name === key);
                    const label = filter?.label
                        ? intl.formatMessage({ id: filter.label })
                        : intl.formatMessage({ id: `bk.filter.${key}`, defaultMessage: key });

                    const getLabel = (val: string) => {
                        if (filter?.type === 'select_list' && filter?.inputOptions) {
                            const found = filter.inputOptions.find(option => option.value === val);
                            if (found) return found.display ?? found.label ?? val;
                        }

                        const found = filter?.options?.find(option => option.value === val);
                        return found
                            ? intl.formatMessage({ id: found.label })
                            : intl.formatMessage({ id: val, defaultMessage: val });
                    };

                    const displayValue = Array.isArray(value)
                        ? value.map(getLabel).join(', ')
                        : getLabel(value);

                    return (
                        <div key={key} className="flex items-center bg-bk-grey-light text-sm rounded-full px-3 py-1">
                            <span>{`${label}: ${displayValue}`}</span>
                            <button
                                className="ml-2 text-red-500 font-bold"
                                onClick={() => {
                                    const updatedValues = { ...submittedValues, [key]: '' };
                                    setSubmittedValues(updatedValues);
                                    if (onFilterUpdate) {
                                        onFilterUpdate(updatedValues);
                                    }
                                    setActiveFilters(Object.entries(updatedValues).filter(([_, val]) => val && val !== '' && !(Array.isArray(val) && val.length === 0)).length);
                                }}
                            >
                                ×
                            </button>
                        </div>
                    );
                })}
            </div>
            <Dropdown
                btnBadge={activeFilters ? activeFilters : 0}
                disabled={disabled}
                btnLabel={btnLabel}
                content={(close: () => void) => (
                    <div className={'flex flex-col space-y-4 overflow-y-auto lg:p-4 ' + (fullHeight ? 'max-h-full' : 'max-h-96')}>
                        <Form
                            enableReinitialize={true}
                            initValues={filters.reduce(
                                (result: any, currentValue) => {
                                    if (currentValue.name === 'search') {
                                        result[currentValue.name] = searchProvided;
                                        return result;
                                    }
                                    if (currentValue.name && !result[currentValue.name] && currentValue.type !== 'select_list') {
                                        result[currentValue.name] = '';
                                    } else if (currentValue.name && !result[currentValue.name] && currentValue.type === 'select_list') {
                                        result[currentValue.name] = [];
                                    }
                                    return result
                                }, {}
                            )}
                            className="w-full"
                            cancelBtn={() => {
                                resetFilter?.();
                                setActiveFilters(0);
                                setSubmittedValues({});
                                close();
                            }}
                            resetInitValuesOnCancel={true}
                            cancelBtnLabel="bk.placeholder.filter.reset"
                            validationSchema={{}}
                            onSubmit={(values: FormikValues, actions: any) => handleSubmit(values, actions, close)}
                            fields={filters.map(filter => ({
                                type: filter.type,
                                name: filter.name,
                                label: filter.label,
                                options: filter.options,
                                fields: filter.fields,
                                inputPlaceholder: filter.inputPlaceholder,
                                inputLabel: filter.inputLabel,
                                inputOptions: filter.inputOptions,
                                inputNotLabels: filter.inputNotLabels
                            }))}
                            submitBtnLabel={btnLabel}
                        />
                    </div>
                )}
                panelPosition={panelPosition}
                panelWidthClassName={panelWidthClassName}
            />
        </>
    );
};
